import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import Step1Form from "./forms/Step1Form";
import Step2Form from "./forms/Step2Form";
import Step3Form from "./forms/Step3Form";
import {
  useValuationFormStore,
  ValuationFormValues,
} from "@/store/valuationFormStore";
import { Button } from "../ui/button";
import { ArrowLeft, ArrowRight, Plus, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { submitPropertyJson } from "@/api/property";
import { transformToPayload } from "@/utils/transform-to-pay-load";
import { CurrencyProvider } from "@/context/CurrencyContext";

const ValuationForm: React.FC<{
  property?: ValuationFormValues;
  isLoading?: boolean;
}> = ({ property, isLoading }) => {
  const {
    currentStep,
    formData,
    nextStep,
    prevStep,
    updateFormData,
    resetForm,
    isStepValid,
  } = useValuationFormStore();

  const methods = useForm<ValuationFormValues>({
    defaultValues: formData,
    mode: "onChange",
  });

  const { handleSubmit, reset, getValues, watch } = methods;
  const [isSaving, setIsSaving] = useState(false);

  // Prefill form when property loads
  useEffect(() => {
    if (property) {
      console.log("Resetting form with property data:", property);
      reset(property);
      updateFormData(property);
    }
  }, [property, reset, updateFormData]);

  // Live-sync RHF → Zustand (safe)
  useEffect(() => {
    const subscription = watch((values) => {
      updateFormData(values);
    });
    return () => subscription.unsubscribe();
  }, [watch, updateFormData]);

  const handleNext = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    updateFormData(getValues());
    nextStep();
  };

  const handlePrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    updateFormData(getValues());
    prevStep();
  };

  const handleSaveChanges = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();

    // Prevent multiple clicks while saving
    if (isSaving) return;

    setIsSaving(true);

    try {
      const currentValues = getValues();
      console.log("Current form values before save:", currentValues);
      const { id, ...clean } = currentValues;

      // Ensure required fields are not undefined
      if (clean.country === "UG" && !clean.region) {
        alert("Region is required");
        setIsSaving(false);
        return;
      }

      updateFormData(clean);
      console.log("Cleaned form data to submit:", clean);

      const payload = transformToPayload(clean as import("@/types/index").ValuationFormValues);
      console.log("Payload to submit:", payload);

      await submitPropertyJson(payload);
      alert("Property data saved successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to save property data.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Form />;
      case 2:
        return <Step2Form />;
      case 3:
        return <Step3Form />;
      default:
        return <Step1Form />;
    }
  };

  return (
    <CurrencyProvider>
      <Card className="bg-white rounded-lg shadow-sm border border-gray-200">
        <FormProvider {...methods}>
          <CardHeader className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900">
                Data Collection - Stage {currentStep}
              </CardTitle>

              <div className="flex space-x-2">
                {currentStep > 1 && (
                  <Button variant="outline" onClick={handlePrevious}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}

                <Button
                  className="bg-primary hover:bg-primary/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>

                {currentStep < 3 && (
                  <Button variant="outline" onClick={handleNext}>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <form className="space-y-6">{renderCurrentStep()}</form>
        </FormProvider>
      </Card>
    </CurrencyProvider>
  );
};

export default ValuationForm;
